import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Database, 
  Building2, 
  Store, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  MapPin,
  Map,
  Globe,
  Tag,
  Folder,
  ShieldAlert,
  Flame,
  Leaf,
  Megaphone,
  RectangleHorizontal,
  SquareStack,
  Users,
  Inbox,
  ShoppingBag,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_neighbor-market-6/artifacts/78ujw3e9_LocaLS_Logo.png";

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openGroups, setOpenGroups] = useState({});

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { 
      name: 'Dashboard', 
      path: '/admin/dashboard', 
      icon: LayoutDashboard 
    },
    { 
      name: 'Masters', 
      icon: Database,
      isParent: true,
      groupKey: 'masters',
      children: [
        { name: 'Cities', path: '/admin/masters/cities', icon: MapPin },
        { name: 'States', path: '/admin/masters/states', icon: Map },
        { name: 'Countries', path: '/admin/masters/countries', icon: Globe },
        { name: 'Categories', path: '/admin/masters/categories', icon: Folder },
        { name: 'Tags', path: '/admin/masters/tags', icon: Tag },
        { name: 'Allergens', path: '/admin/masters/allergens', icon: ShieldAlert },
        { name: 'Heat Profiles', path: '/admin/masters/heat-profiles', icon: Flame },
        { name: 'Dietary Restrictions', path: '/admin/masters/dietary-restrictions', icon: Leaf },
      ]
    },
    { 
      name: 'Apartments', 
      path: '/admin/apartments', 
      icon: Building2 
    },
    { 
      name: 'Vendors', 
      path: '/admin/vendors', 
      icon: Store 
    },
    { 
      name: 'Customers', 
      path: '/admin/customers', 
      icon: Users 
    },
    { 
      name: 'Orders', 
      path: '/admin/orders', 
      icon: ShoppingBag 
    },
    { 
      name: 'Leads', 
      path: '/admin/leads', 
      icon: Inbox 
    },
    {
      name: 'Ads',
      icon: Megaphone,
      isParent: true,
      groupKey: 'ads',
      children: [
        { name: 'Banner', path: '/admin/ads/banner', icon: RectangleHorizontal },
        { name: 'Interstitial', path: '/admin/ads/interstitial', icon: SquareStack },
      ]
    },
    { 
      name: 'Settings', 
      path: '/admin/settings', 
      icon: Settings 
    },
  ];

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const NavItem = ({ item }) => {
    if (item.isParent) {
      const isOpen = openGroups[item.groupKey];
      return (
        <div>
          <button
            onClick={() => toggleGroup(item.groupKey)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-text-secondary hover:bg-background-secondary hover:text-text-primary transition-colors font-body font-medium`}
          >
            <span className="flex items-center gap-3">
              <item.icon size={20} />
              {sidebarOpen && <span>{item.name}</span>}
            </span>
            {sidebarOpen && (
              isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />
            )}
          </button>
          {isOpen && sidebarOpen && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children.map((child) => (
                <NavLink
                  key={child.path}
                  to={child.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2 rounded-xl transition-colors font-body ${
                      isActive
                        ? 'bg-brand-primary text-white font-semibold'
                        : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
                    }`
                  }
                >
                  <child.icon size={18} />
                  <span>{child.name}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors font-body font-medium ${
            isActive
              ? 'bg-brand-primary text-white font-semibold'
              : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
          }`
        }
      >
        <item.icon size={20} />
        {sidebarOpen && <span>{item.name}</span>}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-background-secondary flex">
      {/* Sidebar */}
      <aside 
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-surface-border transition-all duration-300 flex flex-col`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-surface-border">
          <div className="flex items-center gap-3">
            <div className="bg-brand-primary rounded-xl p-2">
              <img 
                src={LOGO_URL} 
                alt="LocaLS" 
                className="h-8 w-auto object-contain"
              />
            </div>
            {sidebarOpen && (
              <span className="font-heading font-bold text-xl text-text-primary">Admin</span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <NavItem key={item.name} item={item} />
          ))}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-surface-border">
          {sidebarOpen && (
            <div className="mb-4 px-4">
              <p className="font-body font-semibold text-text-primary truncate">{user?.name}</p>
              <p className="font-body text-sm text-text-muted truncate">{user?.email}</p>
            </div>
          )}
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-center gap-2 rounded-xl border-surface-border text-text-secondary hover:text-red-600 hover:border-red-200 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-white border-b border-surface-border px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-background-secondary transition-colors"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex items-center gap-4">
            <span className="font-body text-text-secondary">Welcome, {user?.name}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
